/**
 * キャンペーン管理（Phase 23 / spec_line_bot.md A-11）
 * バーチャルホワイトボードでマルチチャネル施策フローを設計するためのデータモデル。
 */
import type { IsoOrTimestamp } from './common';

// ─────────────────────────────────────────────
// カード（要素）型
// ─────────────────────────────────────────────
export type CampaignElementType =
  | 'line_news'           // LINE ニュース配信
  | 'mail_schedule'       // 配信予約への流し込み（既存予約に audience 追加）
  | 'mail_send'           // メール配信（dispatcher が新規 schedule を動的作成、Phase 23d-fix）
  | 'mail_draft'          // 旧仕様（互換維持）：mail_schedule に統合
  | 'form'                // フォーム
  | 'landing'             // ランディングページ (plan adaptive-crown §機能 1)
  | 'referral'            // SNS 広告 / QR / 紙媒体等の外部チャネル流入カード（タグ付き URL 発行）
  | 'mail_contacts_group' // 配信先グループ
  | 'event'               // LINE Bot イベント / 日時マイルストーン
  | 'document_request'    // 資料請求（Phase 8+: 公開資料への CTA リンク + campaignId 行動追跡）
  | 'briefing_request'    // 個別説明申込（dealStageHint='briefing' の営業フォーム）
  | 'quote_request'       // 見積依頼（dealStageHint='quote' の営業フォーム）
  | 'delay'               // 旧仕様：線の after_delay に統合
  | 'condition'           // 旧仕様：線の condition に統合
  | 'metric'              // 旧仕様：ダッシュボードで代替
  | 'note';               // メモ（接続非対応）

/** referral カードの遷移先種別 */
export type ReferralTargetKind = 'landing' | 'form';

export type CampaignTriggerKind =
  | 'on_complete'
  | 'on_form_submit'
  | 'on_open'
  | 'on_link_click'
  | 'after_delay'
  | 'on_event_start' // event カード専用：開催開始時に発火
  | 'on_event_end' // event カード専用：開催終了時に発火
  | 'manual';

export interface CampaignTrigger {
  kind: CampaignTriggerKind;
  /** after_delay 専用 */
  delayMs?: number;
}

export type CampaignConditionKind =
  | 'always'
  | 'has_tag'
  | 'in_group'
  | 'form_answered'
  | 'not_form_answered';

export interface CampaignCondition {
  kind: CampaignConditionKind;
  tag?: string;
  groupId?: string;
  formId?: string;
  fieldId?: string;
  /**
   * form_answered の設問値比較演算子。
   *   eq / neq / contains / gt / lt … `value` と比較する（value 必須）。
   *   exists     … その設問に回答がある（入力あり = 非空）人だけ通す。value 不要。
   *   not_exists … その設問が空欄（未入力）の人だけ通す。value 不要。
   * 自由記述など「値の一致」でなく「回答したか否か」で絞りたいケース用。
   */
  operator?: 'eq' | 'neq' | 'contains' | 'gt' | 'lt' | 'exists' | 'not_exists';
  value?: string;
}

export interface CampaignElement {
  /** uuid（ボード上のローカル ID） */
  id: string;
  type: CampaignElementType;
  x: number;
  y: number;
  /** 既存リソース ID（mail_id / news_id / form_id 等）*/
  refId?: string;
  /** 表示用 denormalize（リソース名スナップショット） */
  refLabel?: string;
  /** カード内 ↗ 開くボタン用（フォーム公開 URL / コンテンツ URL 等）*/
  externalUrl?: string;
  /** delay 専用 */
  delayMs?: number;
  /** condition 専用 */
  conditionDef?: CampaignCondition;
  /** メモ・自由記述 */
  note?: string;
  /**
   * mail_send 専用（Phase 23d-fix）：dispatcher が新規 schedule を作成する際の
   * 送信元アドレス（旧スキーマ）。Phase 26 以降は fromAccountId を優先。
   * fromAccountId 未設定時のみフォールバックとして email 文字列で逆引きする。
   */
  fromAddress?: string;
  /**
   * mail_send 専用（Phase 26）：送信元アカウント ID (mail_accounts/{id})。
   * fromAddress より優先される。
   */
  fromAccountId?: string;
  /**
   * mail_send / mail_schedule 専用（plan adaptive-crown §機能 2）:
   * 配信時に本文の CTA URL を上書きするための URL。
   *  - 紐付けたランディング → `/l/{slug}` 自動セット
   *  - 紐付けたフォーム → `/public/forms/{formId}` 自動セット
   *  - 直接 URL 入力も可
   *
   * dispatcher が mail_schedule を作成する際にこの値を schedule.ctaUrl にコピー。
   * 各受信者個別送信時に cid (or lid) が自動付与される（sendIndividualMailTask）。
   */
  ctaUrlOverride?: string;

  /**
   * mail_send 専用（送信控え機能）：送信控え宛先メアド配列。
   * dispatcher が schedule を作成する際にこの値を schedule.cc にコピーする。
   * 配信内容の控えがこの宛先へ 1 通だけ届く（件名に【送信控え】）。
   */
  cc?: string[];

  /**
   * referral カード専用：遷移先種別（landing or form）。
   * refId はその種別のリソースを指す（landings/{id} or forms/{id}）。
   */
  referralTargetKind?: ReferralTargetKind;

  /**
   * referral カード専用：referral_channels/{id} への参照（任意）。
   * プリセットから選んだ場合のみ設定される。自由記述時は空。
   * UI 上で「どのプリセットを選んだか」を継続表示するために使う。
   */
  referralPresetId?: string;

  /**
   * referral カード専用：実際に URL に埋め込まれる ref タグ文字列。
   * 自由記述 / プリセット選択どちらの経路でも最終的にここへ入る。
   * audience_events.metadata.refTag に書き込まれる識別子。
   */
  referralRefTag?: string;

  // ─── event カード専用：自動トリガー用時刻フィールド（2026-05-20 追加） ───
  /**
   * event カード専用：開催開始日時。sweepEventTimings が監視して、時刻到達時に
   * on_event_start トリガーの接続線を自動発火する。
   * null/undefined なら自動発火なし（手動 trigger のみ）。
   */
  eventStartAt?: IsoOrTimestamp | null;
  /** event カード専用：開催終了日時。on_event_end トリガー用。 */
  eventEndAt?: IsoOrTimestamp | null;
  /** event カード専用：開始発火済みタイムスタンプ（重複発火防止）。eventStartAt 編集時はクリア。 */
  lastEventStartFiredAt?: IsoOrTimestamp | null;
  /** event カード専用：終了発火済みタイムスタンプ。eventEndAt 編集時はクリア。 */
  lastEventEndFiredAt?: IsoOrTimestamp | null;
  /**
   * event カード専用：参加者フォームの ID (forms/{id} 参照)。
   * 設定すると、開始/終了トリガー発火時にこのフォームの送信者を audience として
   * 下流カード（mail_send 等）に渡す。未設定なら audience=[]。
   */
  participantFormId?: string;
}

export interface CampaignConnection {
  id: string;
  fromId: string;
  toId: string;
  fromHandle: 'left' | 'right';
  toHandle: 'left' | 'right';
  trigger: CampaignTrigger;
  condition?: CampaignCondition;
  /** UI 表示ラベル */
  label?: string;
}

export type CampaignStatus = 'planning' | 'running' | 'completed' | 'archived';

export interface CampaignBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * background_info に格納される値の許容型。
 * Firestore に保存可能な scalar に限定（オブジェクト・配列はキー追加で平坦に扱う）。
 */
export type BackgroundInfoValue = string | number | boolean | null;

/**
 * background_info の構造（動的キー値オブジェクト）。
 *
 * 既定キー（後方互換のため UI から削除不可）:
 *   description, goal, targetSegment, budget, kpi, owner, memo
 *
 * ユーザーが任意キーを追加可能。Firestore のドット記法で部分更新する想定。
 */
export type CampaignBackgroundInfo = Record<string, BackgroundInfoValue>;

/** 既定キーの順序（UI レンダリング順） */
export const CAMPAIGN_BACKGROUND_DEFAULT_KEYS = [
  'description',
  'goal',
  'targetSegment',
  'budget',
  'kpi',
  'owner',
  'memo',
] as const;

export type CampaignBackgroundDefaultKey =
  (typeof CAMPAIGN_BACKGROUND_DEFAULT_KEYS)[number];

/** 既定キーの日本語ラベル（UI 表示用） */
export const CAMPAIGN_BACKGROUND_LABELS: Record<
  CampaignBackgroundDefaultKey,
  string
> = {
  description: '概要',
  goal: '目的 / ゴール',
  targetSegment: 'ターゲット',
  budget: '予算（円）',
  kpi: 'KPI / KGI',
  owner: '担当者',
  memo: 'メモ・備考',
};

/** 既定キーのプレースホルダ */
export const CAMPAIGN_BACKGROUND_PLACEHOLDERS: Record<
  CampaignBackgroundDefaultKey,
  string
> = {
  description: 'このキャンペーンの説明文',
  goal: '例: 春の来場予約 100 件獲得',
  targetSegment: '例: 30 代既婚 / 注文住宅検討中',
  budget: '例: 500000',
  kpi: '例: 申込 CVR 5% / 資料 DL 200 件',
  owner: '例: 寺岡 / 営業部 + マーケ',
  memo: '関係者への申し送り事項など',
};

/**
 * 既定 7 キーで初期化された空の background_info を返す。
 * 新規キャンペーン作成時に呼び出す。
 */
export function createEmptyBackgroundInfo(): CampaignBackgroundInfo {
  return {
    description: '',
    goal: '',
    targetSegment: '',
    budget: null,
    kpi: '',
    owner: '',
    memo: '',
  };
}

// ─────────────────────────────────────────────
// background_info スキーマ拡張（2026-05 / フィールドUX改善）
// ─────────────────────────────────────────────
/**
 * 任意フィールドの入力型ヒント。UI 描画と prompt 整形に使う。
 */
export type BackgroundFieldType = 'text' | 'long' | 'number' | 'date';

/**
 * 任意フィールドのメタデータ。
 *
 * - label: 日本語自由入力。プロンプトと UI 表示の両方で使う。
 * - type: 入力 UI のヒント（既定 'text'）。
 * - groupId: 所属するグループの ID（standalone なら undefined）。
 * - presetSuffix: グループプリセットに由来する場合の元 suffix（"title" "date" 等）。
 */
export interface BackgroundFieldMeta {
  label?: string;
  type?: BackgroundFieldType;
  groupId?: string;
  presetSuffix?: string;
}

/**
 * グループ（イベント 1 回 / 商品 1 つ等の繰り返し単位）。
 */
export interface BackgroundGroupMeta {
  /** 表示ラベル（例: "イベント1", "商品A"） */
  label: string;
  /** 元プリセット ID（'event' | 'product' | 'audience' 等） */
  presetId?: string;
}

/**
 * background_info に紐づくラベル・型・グループのメタデータ。
 * `background_info` 自体はスカラ値のフラット辞書を維持（後方互換）、
 * メタデータはこの sibling フィールドに分離して持つ。
 */
export interface CampaignBackgroundSchema {
  /** key → meta */
  fields?: Record<string, BackgroundFieldMeta>;
  /** groupId → meta */
  groups?: Record<string, BackgroundGroupMeta>;
  /** 任意フィールドの表示順（既定キーを除く） */
  fieldOrder?: string[];
  /** グループの表示順 */
  groupOrder?: string[];
}

/**
 * グループプリセット定義。
 * 「+ イベントを追加」のような繰り返しを 1 クリックで実現するためのテンプレ。
 */
export interface BackgroundGroupPreset {
  id: string;
  /** プリセットの表示名（例: 'イベント'） */
  label: string;
  /** 1 インスタンスあたりのデフォルトフィールド */
  fields: ReadonlyArray<{
    /** key の suffix（'title' → `${groupKey}_title`） */
    suffix: string;
    /** 既定ラベル（日本語） */
    label: string;
    /** 既定の入力型 */
    type: BackgroundFieldType;
    /** プレースホルダ */
    placeholder?: string;
  }>;
}

export const BACKGROUND_GROUP_PRESETS: Record<string, BackgroundGroupPreset> = {
  event: {
    id: 'event',
    label: 'イベント',
    fields: [
      { suffix: 'title', label: 'タイトル', type: 'text', placeholder: '例: TimeWaver 経営者交流会' },
      { suffix: 'date', label: '開催日時', type: 'text', placeholder: '例: 2026-06-15 19:00〜21:00' },
      { suffix: 'venue', label: '会場 / 開催形態', type: 'text', placeholder: '例: 東京・銀座 / オンライン' },
      { suffix: 'concept', label: 'コンセプト', type: 'long', placeholder: 'このイベントが目指す体験・価値' },
      { suffix: 'target', label: 'ターゲット', type: 'long', placeholder: '誰に来てほしいか' },
      { suffix: 'timeline', label: 'タイムライン', type: 'long', placeholder: '時間ごとの流れ' },
      { suffix: 'takeaway', label: '持ち帰り価値', type: 'long', placeholder: '参加者が得るもの' },
    ],
  },
  product: {
    id: 'product',
    label: '商品 / サービス',
    fields: [
      { suffix: 'name', label: '商品名', type: 'text' },
      { suffix: 'price', label: '価格（円）', type: 'number' },
      { suffix: 'feature', label: '特長', type: 'long' },
      { suffix: 'audience', label: '想定購入者', type: 'long' },
    ],
  },
  audience: {
    id: 'audience',
    label: 'ターゲット層',
    fields: [
      { suffix: 'persona', label: 'ペルソナ', type: 'text' },
      { suffix: 'pain', label: '抱える課題 / 悩み', type: 'long' },
      { suffix: 'goal', label: '達成したい状態', type: 'long' },
    ],
  },
  offer: {
    id: 'offer',
    label: 'オファー / 訴求',
    fields: [
      { suffix: 'headline', label: 'キャッチコピー', type: 'text' },
      { suffix: 'value', label: '提供価値', type: 'long' },
      { suffix: 'cta', label: 'CTA テキスト', type: 'text', placeholder: '例: 無料で資料を受け取る' },
    ],
  },
};

export interface Campaign {
  name: string;
  status: CampaignStatus;
  elements: CampaignElement[];
  connections: CampaignConnection[];
  /** ボード仮想サイズ（lazy load 用、要素位置から計算） */
  bounds?: CampaignBounds;
  startAt?: IsoOrTimestamp | null;
  endAt?: IsoOrTimestamp | null;

  /**
   * キャンペーン詳細情報（動的キー値オブジェクト）。
   * Phase 0 (Landing 機能) で導入。固定フィールド（description / goal 等）から移行。
   * 既定 7 キー + ユーザー追加の任意キーを格納。
   */
  background_info?: CampaignBackgroundInfo;

  /**
   * background_info の付随メタデータ（ラベル・型・グループ）。
   * 後方互換のため optional。存在しない場合は従来の `getCampaignBackgroundLabel` 挙動にフォールバック。
   */
  background_info_schema?: CampaignBackgroundSchema;

  // ─── Phase 23a-ext-3: メタデータ拡張（@deprecated, background_info に移行）───
  /**
   * @deprecated background_info.description を使用。並走期間中のみ残す。
   */
  description?: string;
  /**
   * @deprecated background_info.goal を使用。
   */
  goal?: string;
  /**
   * @deprecated background_info.targetSegment を使用。
   */
  targetSegment?: string;
  /**
   * @deprecated background_info.budget を使用。
   */
  budget?: number | null;
  /**
   * @deprecated background_info.kpi を使用。
   */
  kpi?: string;
  /**
   * @deprecated background_info.owner を使用。
   */
  owner?: string;
  /**
   * @deprecated background_info.memo を使用。
   */
  memo?: string;

  // ─── テンプレート機能 ───
  /** true ならこのキャンペーン doc はテンプレートとして扱われる（一覧で分離表示） */
  isTemplate?: boolean;
  /** テンプレートから作成された場合の元 templateId（参照） */
  fromTemplateId?: string;

  /**
   * 自動処理の稼働状態（2026-05-20 追加）。
   *   true = 休止: あらゆる自動発火（イベント時刻トリガー / フォーム送信 / メール完了 /
   *               遅延ディスパッチ / 手動「今すぐ発火」ボタン）をスキップ
   *   false / undefined = 稼働: 通常通り自動発火
   *
   * 後方互換: undefined は稼働扱い（既存 campaign は migration なしで動き続ける）。
   * 新規作成時は明示的に true をセット（事故防止）。
   */
  paused?: boolean;

  createdBy: string;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}

/**
 * background_info から値を取得する（旧フィールドへフォールバック）。
 * 並走期間中の読み取りはこのヘルパーを経由する。
 */
export function getCampaignBackgroundField<T extends BackgroundInfoValue>(
  campaign: Pick<
    Campaign,
    | 'background_info'
    | 'description'
    | 'goal'
    | 'targetSegment'
    | 'budget'
    | 'kpi'
    | 'owner'
    | 'memo'
  >,
  key: string,
  defaultValue: T,
): T {
  const fromBg = campaign.background_info?.[key];
  if (fromBg !== undefined && fromBg !== null && fromBg !== '') {
    return fromBg as T;
  }
  // 既定キーは旧フィールドにフォールバック
  if (
    (CAMPAIGN_BACKGROUND_DEFAULT_KEYS as readonly string[]).includes(key) &&
    key in campaign
  ) {
    const legacy = (campaign as unknown as Record<string, BackgroundInfoValue>)[
      key
    ];
    if (legacy !== undefined && legacy !== null && legacy !== '') {
      return legacy as T;
    }
  }
  // background_info に明示的に null/'' が入っている場合はそれを優先
  if (campaign.background_info && key in campaign.background_info) {
    return campaign.background_info[key] as T;
  }
  return defaultValue;
}

/**
 * UI 表示用ラベル。既定キーは日本語ラベル、任意キーは humanize したものを返す。
 */
export function getCampaignBackgroundLabel(key: string): string {
  if (
    (CAMPAIGN_BACKGROUND_DEFAULT_KEYS as readonly string[]).includes(key)
  ) {
    return CAMPAIGN_BACKGROUND_LABELS[key as CampaignBackgroundDefaultKey];
  }
  // snake_case / camelCase → Title Case
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .replace(/^./, (s) => s.toUpperCase());
}

/**
 * schema を考慮した表示ラベル解決。
 * schema.fields[key].label が定義されていればそれを優先、
 * 未定義なら従来の `getCampaignBackgroundLabel` にフォールバック。
 */
export function resolveBackgroundLabel(
  key: string,
  schema?: CampaignBackgroundSchema,
): string {
  const metaLabel = schema?.fields?.[key]?.label;
  if (typeof metaLabel === 'string' && metaLabel.trim() !== '') {
    return metaLabel;
  }
  return getCampaignBackgroundLabel(key);
}

/**
 * 短いランダム key を生成。Firestore のフィールド名として安全な英数字。
 */
export function generateBackgroundKey(prefix: string): string {
  const safePrefix = prefix.replace(/[^a-zA-Z]/g, '').toLowerCase() || 'f';
  const rand = Math.random().toString(36).slice(2, 8);
  return `${safePrefix}_${rand}`;
}

/**
 * グループプリセットから 1 インスタンス分のフィールド定義を展開する。
 * 戻り値は { groupId, groupMeta, fields: [{ key, meta, initialValue }] }。
 *
 * 既存の groupOrder / fieldOrder への追加は呼び出し側で行う。
 */
export function expandGroupPreset(
  presetId: string,
  groupLabel: string,
): {
  groupId: string;
  groupMeta: BackgroundGroupMeta;
  fields: Array<{
    key: string;
    meta: BackgroundFieldMeta;
    initialValue: BackgroundInfoValue;
  }>;
} | null {
  const preset = BACKGROUND_GROUP_PRESETS[presetId];
  if (!preset) return null;
  const groupId = generateBackgroundKey(preset.id);
  const fields = preset.fields.map((f) => ({
    key: `${groupId}_${f.suffix}`,
    meta: {
      label: f.label,
      type: f.type,
      groupId,
      presetSuffix: f.suffix,
    } satisfies BackgroundFieldMeta,
    initialValue: (f.type === 'number' ? null : '') as BackgroundInfoValue,
  }));
  return {
    groupId,
    groupMeta: {
      label: groupLabel,
      presetId: preset.id,
    },
    fields,
  };
}

// ─────────────────────────────────────────────
// 実行ログ（Phase 23b 以降で利用）
// ─────────────────────────────────────────────
export interface CampaignExecution {
  campaignId: string;
  /** どの connection が発火したか */
  connectionId: string;
  /** 発火元イベントの種別 */
  triggerKind: CampaignTriggerKind;
  /** 影響を受けた audience ID 配列（contactId / userId 等） */
  audienceIds: string[];
  /** 実行されたアクション種別 */
  actionType: CampaignElementType;
  /** 成否 */
  status: 'success' | 'failed' | 'skipped';
  errorMessage?: string;
  /** idempotency key（同じイベント × audience の重複実行防止） */
  idempotencyKey: string;
  executedAt: IsoOrTimestamp;
}

// ─────────────────────────────────────────────
// Phase 23b: dispatcher
// ─────────────────────────────────────────────

/** dispatcher が処理するイベント種別 */
export type CampaignEventKind =
  | 'mail_sent' // mail_schedules.status='sent'
  | 'line_news_sent' // line_news.status='sent'
  | 'form_submitted' // form_submissions 新規作成
  | 'mail_opened' // Phase 23e
  | 'cta_clicked' // Phase 23e
  | 'event_date' // Cloud Scheduler events.date 通過（旧）
  | 'event_started' // 2026-05: event カードの eventStartAt 到達
  | 'event_ended' // 2026-05: event カードの eventEndAt 到達
  | 'after_delay'; // 内部：遅延後の再ディスパッチ

export interface CampaignEventPayload {
  kind: CampaignEventKind;
  /** 発火元リソース ID（mail_id / news_id / form_id 等） */
  refId: string;
  /** 影響を受ける audience（contactId / userId / lineUid） */
  audienceIds: string[];
  /** dispatch 連鎖の hop カウンタ（環状検出用、初回 = 0） */
  hops?: number;
  /**
   * イベントインスタンスの一意 ID（idempotency 用）。
   * 同じ event * audience セットの重複処理を防ぐためのキー。
   */
  eventInstanceId?: string;
}

/**
 * 遅延実行の queue。Phase 23b-ext で導入。
 * after_delay トリガを Cloud Scheduler ベースで実装するため、
 * dispatcher が遅延が必要な connection をこのコレクションに enqueue する。
 *
 * Cloud Tasks の代替として、`pending_dispatches/{id}` + 1 分毎の Scheduled function で
 * fireAt <= now() の doc を取り出して executeCampaignAction を呼ぶ。
 *
 * 状態遷移: pending → done | failed
 *  - pending: 未実行
 *  - done: 実行済（idempotency 兼用、再実行されない）
 *  - failed: 例外（リトライは attempts < 3 のみ）
 */
export interface CampaignPendingDispatch {
  campaignId: string;
  connectionId: string;
  /** dispatcher が解決した audienceIds（contactId 等） */
  audienceIds: string[];
  /** 上流 dispatchCampaignEvent の hops を引き継ぐ */
  hops: number;
  /** idempotency 伝搬用 */
  eventInstanceId: string;
  /** 実行予定時刻 */
  fireAt: IsoOrTimestamp;
  status: 'pending' | 'done' | 'failed';
  attempts: number;
  errorMessage?: string;
  enqueuedAt: IsoOrTimestamp;
  executedAt?: IsoOrTimestamp;
}
